import { api } from './api';
import type {
  AgentDetailDto,
  AgentDto,
  AgentExecutionDto,
  AgentExecutionListItem,
  AgentExecutionMonitoringSummary,
  AgentFlowDto,
  AgentGraphDto,
  AgentGraphValidationResult,
  AgentKnowledgeBaseDto,
  AgentPlatformAuditDto,
  AgentPlatformMonitoringSettings,
  AgentToolDto,
  AutomationInstanceDto,
  BindAgentFlowRequest,
  BindAgentKnowledgeBaseRequest,
  BindAgentToolRequest,
  CreateAgentFlowRequest,
  CreateAgentFlowVersionRequest,
  CreateAgentRequest,
  CreateAgentKnowledgeBaseRequest,
  CreateAgentToolRequest,
  CreateAutomationInstanceRequest,
  CustomerAgentPlatformConfig,
  ExecuteAgentRequest,
  PaginatedResult,
  PublishAgentRequest,
  UpdateAgentFlowRequest,
  UpdateAgentGraphRequest,
  UpdateAgentKnowledgeBaseRequest,
  UpdateAgentPlatformGlobalMonitoringSettingsRequest,
  UpdateAgentPlatformMonitoringSettingsRequest,
  UpdateAgentRequest,
  UpdateAgentToolRequest,
  UpdateCustomerAgentPlatformConfigRequest,
  AgentExecutionTimeSeriesPoint,
} from '../types/agentPlatform';

export const agentPlatformService = {
  async getAgents(): Promise<AgentDto[]> {
    const { data } = await api.get<AgentDto[]>('/agents');
    return data;
  },

  async getAgent(id: string): Promise<AgentDetailDto> {
    const { data } = await api.get<AgentDetailDto>(`/agents/${id}`);
    return data;
  },

  async createAgent(payload: CreateAgentRequest): Promise<AgentDto> {
    const { data } = await api.post<AgentDto>('/agents', payload);
    return data;
  },

  async updateAgent(id: string, payload: UpdateAgentRequest): Promise<AgentDetailDto> {
    const { data } = await api.put<AgentDetailDto>(`/agents/${id}`, payload);
    return data;
  },

  async deleteAgent(id: string): Promise<void> {
    await api.delete(`/agents/${id}`);
  },

  async publishAgent(id: string, payload: PublishAgentRequest): Promise<AgentDetailDto> {
    const { data } = await api.post<AgentDetailDto>(`/agents/${id}/publish`, payload);
    return data;
  },

  async bindTool(agentId: string, payload: BindAgentToolRequest): Promise<AgentDetailDto> {
    const { data } = await api.post<AgentDetailDto>(`/agents/${agentId}/tools`, payload);
    return data;
  },

  async unbindTool(agentId: string, bindingId: string): Promise<AgentDetailDto> {
    const { data } = await api.delete<AgentDetailDto>(`/agents/${agentId}/tools/${bindingId}`);
    return data;
  },

  async bindFlow(agentId: string, payload: BindAgentFlowRequest): Promise<AgentDetailDto> {
    const { data } = await api.post<AgentDetailDto>(`/agents/${agentId}/flows`, payload);
    return data;
  },

  async unbindFlow(agentId: string, bindingId: string): Promise<AgentDetailDto> {
    const { data } = await api.delete<AgentDetailDto>(`/agents/${agentId}/flows/${bindingId}`);
    return data;
  },

  async getGraph(agentId: string): Promise<AgentGraphDto> {
    const { data } = await api.get<AgentGraphDto>(`/agents/${agentId}/graph`);
    return data;
  },

  async updateGraph(agentId: string, payload: UpdateAgentGraphRequest): Promise<AgentGraphDto> {
    const { data } = await api.put<AgentGraphDto>(`/agents/${agentId}/graph`, payload);
    return data;
  },

  async validateGraph(agentId: string, definition: string): Promise<AgentGraphValidationResult> {
    const { data } = await api.post<AgentGraphValidationResult>(`/agents/${agentId}/graph/validate`, {
      definition,
    });
    return data;
  },

  async executeAgent(agentId: string, payload: ExecuteAgentRequest): Promise<AgentExecutionDto> {
    const { data } = await api.post<AgentExecutionDto>(`/agents/${agentId}/execute`, payload);
    return data;
  },

  async getTools(): Promise<AgentToolDto[]> {
    const { data } = await api.get<AgentToolDto[]>('/agent-tools');
    return data;
  },

  async createTool(payload: CreateAgentToolRequest): Promise<AgentToolDto> {
    const { data } = await api.post<AgentToolDto>('/agent-tools', payload);
    return data;
  },

  async updateTool(id: string, payload: UpdateAgentToolRequest): Promise<AgentToolDto> {
    const { data } = await api.put<AgentToolDto>(`/agent-tools/${id}`, payload);
    return data;
  },

  async archiveTool(id: string): Promise<void> {
    await api.delete(`/agent-tools/${id}`);
  },

  async bindKnowledgeBase(
    agentId: string,
    payload: BindAgentKnowledgeBaseRequest
  ): Promise<AgentDetailDto> {
    const { data } = await api.post<AgentDetailDto>(`/agents/${agentId}/knowledge-bases`, payload);
    return data;
  },

  async unbindKnowledgeBase(agentId: string, bindingId: string): Promise<AgentDetailDto> {
    const { data } = await api.delete<AgentDetailDto>(
      `/agents/${agentId}/knowledge-bases/${bindingId}`
    );
    return data;
  },

  async getKnowledgeBases(): Promise<AgentKnowledgeBaseDto[]> {
    const { data } = await api.get<AgentKnowledgeBaseDto[]>('/agent-knowledge-bases');
    return data;
  },

  async getKnowledgeBase(id: string): Promise<AgentKnowledgeBaseDto> {
    const { data } = await api.get<AgentKnowledgeBaseDto>(`/agent-knowledge-bases/${id}`);
    return data;
  },

  async createKnowledgeBase(payload: CreateAgentKnowledgeBaseRequest): Promise<AgentKnowledgeBaseDto> {
    const { data } = await api.post<AgentKnowledgeBaseDto>('/agent-knowledge-bases', payload);
    return data;
  },

  async updateKnowledgeBase(
    id: string,
    payload: UpdateAgentKnowledgeBaseRequest
  ): Promise<AgentKnowledgeBaseDto> {
    const { data } = await api.put<AgentKnowledgeBaseDto>(`/agent-knowledge-bases/${id}`, payload);
    return data;
  },

  async archiveKnowledgeBase(id: string): Promise<void> {
    await api.delete(`/agent-knowledge-bases/${id}`);
  },

  async getFlows(): Promise<AgentFlowDto[]> {
    const { data } = await api.get<AgentFlowDto[]>('/agent-flows');
    return data;
  },

  async getFlow(id: string): Promise<AgentFlowDto> {
    const { data } = await api.get<AgentFlowDto>(`/agent-flows/${id}`);
    return data;
  },

  async createFlow(payload: CreateAgentFlowRequest): Promise<AgentFlowDto> {
    const { data } = await api.post<AgentFlowDto>('/agent-flows', payload);
    return data;
  },

  async updateFlow(id: string, payload: UpdateAgentFlowRequest): Promise<AgentFlowDto> {
    const { data } = await api.put<AgentFlowDto>(`/agent-flows/${id}`, payload);
    return data;
  },

  async createFlowVersion(id: string, payload: CreateAgentFlowVersionRequest): Promise<AgentFlowDto> {
    const { data } = await api.post<AgentFlowDto>(`/agent-flows/${id}/versions`, payload);
    return data;
  },

  async publishFlow(id: string): Promise<AgentFlowDto> {
    const { data } = await api.post<AgentFlowDto>(`/agent-flows/${id}/publish`);
    return data;
  },

  async archiveFlow(id: string): Promise<void> {
    await api.delete(`/agent-flows/${id}`);
  },

  async getAutomationInstances(): Promise<AutomationInstanceDto[]> {
    const { data } = await api.get<AutomationInstanceDto[]>('/automation-instances');
    return data;
  },

  async createAutomationInstance(
    payload: CreateAutomationInstanceRequest
  ): Promise<AutomationInstanceDto> {
    const { data } = await api.post<AutomationInstanceDto>('/automation-instances', payload);
    return data;
  },

  async searchExecutions(params: {
    agentId?: string;
    status?: string;
    isTestExecution?: boolean;
    from?: string;
    to?: string;
    correlationId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedResult<AgentExecutionListItem>> {
    const { data } = await api.get<PaginatedResult<AgentExecutionListItem>>('/agent-executions', {
      params,
    });
    return data;
  },

  async getExecution(id: string): Promise<AgentExecutionDto> {
    const { data } = await api.get<AgentExecutionDto>(`/agent-executions/${id}`);
    return data;
  },

  async getExecutionSummary(params: {
    agentId?: string;
    isTestExecution?: boolean;
    from?: string;
    to?: string;
  }): Promise<AgentExecutionMonitoringSummary> {
    const { data } = await api.get<AgentExecutionMonitoringSummary>(
      '/agent-executions/monitoring/summary',
      { params }
    );
    return data;
  },

  async getAudit(params: {
    entityType?: string;
    entityId?: string;
    action?: string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedResult<AgentPlatformAuditDto>> {
    const { data } = await api.get<PaginatedResult<AgentPlatformAuditDto>>(
      '/agent-platform/audit',
      { params }
    );
    return data;
  },

  async getMonitoringSettings(): Promise<AgentPlatformMonitoringSettings> {
    const { data } = await api.get<AgentPlatformMonitoringSettings>(
      '/agent-platform/monitoring/settings'
    );
    return data;
  },

  async updateTenantMonitoring(
    payload: UpdateAgentPlatformMonitoringSettingsRequest
  ): Promise<AgentPlatformMonitoringSettings> {
    const { data } = await api.put<AgentPlatformMonitoringSettings>(
      '/agent-platform/monitoring/settings/tenant',
      payload
    );
    return data;
  },

  async updateGlobalMonitoring(
    payload: UpdateAgentPlatformGlobalMonitoringSettingsRequest
  ): Promise<AgentPlatformMonitoringSettings> {
    const { data } = await api.put<AgentPlatformMonitoringSettings>(
      '/agent-platform/monitoring/settings/global',
      payload
    );
    return data;
  },

  async getExecutionTimeSeries(params: {
    agentId?: string;
    isTestExecution?: boolean;
    from?: string;
    to?: string;
  }): Promise<AgentExecutionTimeSeriesPoint[]> {
    const { data } = await api.get<AgentExecutionTimeSeriesPoint[]>(
      '/agent-executions/monitoring/timeseries',
      { params }
    );
    return data;
  },

  async getCustomerAgentPlatformConfig(customerId: string): Promise<CustomerAgentPlatformConfig> {
    const { data } = await api.get<CustomerAgentPlatformConfig>(
      `/customers/${customerId}/agent-platform-config`
    );
    return data;
  },

  async updateCustomerAgentPlatformConfig(
    customerId: string,
    payload: UpdateCustomerAgentPlatformConfigRequest
  ): Promise<CustomerAgentPlatformConfig> {
    const { data } = await api.put<CustomerAgentPlatformConfig>(
      `/customers/${customerId}/agent-platform-config`,
      payload
    );
    return data;
  },
};
